import { useCallback, useEffect, useState } from 'react';
import Sticky from 'react-stickynode';
import { selectShowCurrencySelection } from 'tradera/state/multi-currency/selectors';
import { HOVER_MENUS } from './hover-menu/constants';
import { onWindowLoad } from './on-ready';
import { getGeolocation } from 'tradera/state/member/actions';
import { signIn } from 'tradera/state/ui/actions';
import {
    closeNewMessageModal,
    closeSearchForm,
    openSearchForm,
    closeCategoryDropdownMenu,
    closeProfileMenu,
    openProfileMenu,
    openReferralModal
} from 'tradera/state/ui/reducer';
import TraderaLogo from '@tradera/blueprint/theme/logos/tradera_new.svg?jsx';
import TraderaAppLogo from '@tradera/blueprint/theme/logos/tradera-app-logo.svg?jsx';
import CartLink from 'tradera/components/site-header/cart-link';
import { DropdownMenu } from 'tradera/components/site-header/dropdown-menu';
import SignUpLink from 'tradera/components/site-header/sign-up-link';
import ALink from 'tradera/components/alink/alink';
import { Search } from 'tradera/components/search/search';
import PushNotificationModal from 'tradera/components/push-notification-modal/push-notification-modal';
import { LocalizationLink } from './localization-link';
import BuyingLink from './buying-link';
import { HowTraderaWorks } from './how-tradera-works';
import { NewListingLink } from './new-listing-link';
import { ProfileLink } from './profile-link';
import { SearchButton } from './search-button';
import { NewMessageModal } from 'tradera/components/messaging/messaging-new-message-modal';
import { WishlistLink } from 'tradera/components/site-header/wishlist-link';
import { SellingLink } from 'tradera/components/site-header/selling-link';
import { TraderaToastContainer } from 'tradera/components/toasts/tradera-toast-container';
import { showSuccessToast } from 'tradera/services/toasts';
import { CurrencyLink } from 'tradera/components/site-header/currency-link';
import { WishListSideSheet } from 'tradera/apps/tradera/components/wish-list-side-sheet/wish-list-side-sheet';
import { LocalizationRegionPicker } from './localization-region-picker';
import { LoaderLine } from 'tradera/components/loader/loader-line';
import { SaveSearchButton } from 'tradera/apps/tradera/components/discover/components/search-results/save-search/save-search-button';
import { localStore } from 'tradera/utils/storage';
import { STORAGE_KEYS } from 'tradera/constants/storage';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { useIsTablet } from 'tradera/hooks/use-is-tablet';
import useIsDesktop from 'tradera/hooks/use-is-desktop';
import { useUrlLocalizer } from 'tradera/lang/translate';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import {
    getCartSummaryParams,
    useGetCartSummaryQuery
} from 'tradera/state/services/cart-api';
import { useCookieUtil } from 'tradera/hooks/use-cookie-util';
import { CART_ID } from 'tradera/constants/cookies';
import { MODAL_STATE } from 'tradera/constants/ui';
import { useViewportWidthCookie } from 'tradera/apps/nextweb/hooks/use-viewport-width';
import { classNames } from 'tradera/utils/class-names';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { AppDownloadModal } from 'tradera/apps/tradera/components/app-download-modal/app-download-modal';
import { ProfileMenu } from '../profile-menu/profile-menu';
import { selectIsUnderage } from 'tradera/state/member/selectors';
import {
    removeHashFromUrl,
    shouldOpenReferralModal
} from 'tradera/apps/tradera/components/vip/helpers/navigation-helper';
import { ReferralModal } from '../referral-modal/referral-modal';
import { useOnHashUpdated } from 'tradera/hooks/use-on-hash-updated';
import { NotificationsLink } from 'tradera/components/site-header/notifications-link';
import { NotificationsSideSheet } from 'tradera/apps/tradera/components/notifications-side-sheet/notifications-side-sheet';
import { HeaderInternationalIdentificationModal } from 'tradera/components/international-identification-modal/header-international-identification-modal';

type SiteHeaderProps = {
    isPageLoading: boolean | undefined;
    inSPA: boolean;
};

export const SiteHeader = ({ isPageLoading, inSPA }: SiteHeaderProps) => {
    useViewportWidthCookie();

    const dispatch = useAppDispatch();

    const showSearchForm = useAppSelector((state) => state.ui.showSearchForm);
    const showHeaderSearch = useAppSelector(
        (state) => state.ui.showHeaderSearch
    );
    const newMessageModal = useAppSelector((state) => state.ui.newMessageModal);
    const isLoggedIn = useAppSelector((state) => state.member.isLoggedIn);
    const hasLoggedIn = useAppSelector((state) => state.member.hasLoggedIn);
    const isUnderage = useAppSelector(selectIsUnderage);
    const showCurrencySelection = useAppSelector((state) =>
        selectShowCurrencySelection(state)
    );
    const pageName = useAppSelector((state) => state.page.type);

    const isTablet = useIsTablet();
    const isDesktop = useIsDesktop();
    const isMobile = useIsMobile();
    const { toLocalizedUrl } = useUrlLocalizer();

    const [isSticky, setIsSticky] = useState(false);
    const [activeHoverMenu, setActiveHoverMenu] = useState<string | null>(null);

    const cookieUtil = useCookieUtil();
    const cartIdCookieValue = cookieUtil.readCookie(CART_ID) ?? '';

    const { data } = useGetCartSummaryQuery(
        getCartSummaryParams(cartIdCookieValue),
        { skip: !cartIdCookieValue }
    );

    const { profileMenu } = useAppSelector((state) => state.ui);

    const handleLoginClick = useCallback(async () => {
        // making a request to the backend to check if the user is logged in already
        try {
            const response = await fetch(
                '/api/webapi/member/session-data?pageName=header',
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const sessionData = await response.json();
                if (sessionData?.memberId) {
                    window.location.reload();
                    return;
                }
            }
        } catch (error) {
            console.error('Error checking session:', error);
        }

        dispatch(signIn());
    }, [dispatch]);

    // Profile menu: Prevent Default and show login, if not logged in
    const preventNavigationAndShowLogin = (
        event: React.MouseEvent<HTMLElement>
    ) => {
        if (!isLoggedIn) {
            event.preventDefault();
            GtmService.trackAction('Top navigation', 'Login', 'Click');
            handleLoginClick();
        }
    };

    const handleProfileClick = (evt: React.MouseEvent<HTMLElement>) => {
        if (profileMenu.open === MODAL_STATE.OPEN) {
            dispatch(closeProfileMenu());
        } else {
            dispatch(closeCategoryDropdownMenu());

            // not logged in
            if (!isLoggedIn) {
                preventNavigationAndShowLogin(evt);
                return;
            } else {
                dispatch(openProfileMenu());
            }
        }

        if (pageName?.includes('MyTradera')) {
            window.scrollTo(0, 0);
        }
    };

    const { open: profileMenuOpen = false } = profileMenu;

    const { totalCount: cartItemCount } = data || {};

    const showMfaVerifiedMessage = useCallback(() => {
        if (!isLoggedIn) {
            return;
        }

        if (
            !document.referrer ||
            !document.referrer.includes(
                '/login/multifactorauthentication/enterverificationcode'
            )
        ) {
            return;
        }

        if (!localStore || !localStore.enabledByGdprSettings()) {
            return;
        }

        if (
            localStore.hasValueForKey(
                STORAGE_KEYS.HAS_SHOWED_MFA_VERIFICATION_MESSAGE
            )
        ) {
            return;
        }

        showSuccessToast('mfa_verify_success');
        localStore.setItem(
            STORAGE_KEYS.HAS_SHOWED_MFA_VERIFICATION_MESSAGE,
            true
        );
    }, [isLoggedIn]);

    useEffect(() => {
        dispatch(getGeolocation());
        showMfaVerifiedMessage();
        // Scroll up one pixel to show fixed header on mobile
        onWindowLoad(() => {
            // Toggle on sticky behavior on window load to force reflow and show sticky header on mobile & Firefox
            setIsSticky(true);
        });
    }, [dispatch, showMfaVerifiedMessage]);

    useOnHashUpdated(() => {
        if (shouldOpenReferralModal()) {
            if (isLoggedIn) {
                dispatch(openReferralModal());
                removeHashFromUrl();
            } else {
                handleLoginClick();
            }
        }
    });

    const handleClickSearch = () => {
        if (showSearchForm) {
            dispatch(closeSearchForm());
        } else {
            dispatch(openSearchForm());
        }
    };

    const handleMouseOverMenu = (menu: string) => {
        setActiveHoverMenu(menu);
    };

    const handleMouseOutMenu = () => {
        setActiveHoverMenu(null);
    };

    // Event handler for menu button:
    // Mobile: link, no menu open
    // Tablet: not a link, menu open
    // Desktop: link, menu open
    const handleMenuClick = (
        event: React.MouseEvent<HTMLElement>,
        menu: string
    ) => {
        if (
            isMobile &&
            menu !== HOVER_MENUS.LOCALIZATION &&
            menu !== HOVER_MENUS.CURRENCY
        ) {
            return;
        }

        // Prevent click for tablet to allow tap to open
        if (isTablet) {
            event.preventDefault();
        }
        // Force focus so touch devices can get a blur event
        event.currentTarget.focus();

        setActiveHoverMenu((current) => (current === menu ? null : menu));
    };

    const closeHoverMenu = () => {
        setActiveHoverMenu(null);
    };

    const handleRegisterClick = () => {
        if (!isLoggedIn) {
            GtmService.trackAction('Top navigation', 'Sign up', 'Click');
            const cookieName = 'signup_referral_path';
            const expireDate = new Date();
            const value = window.location.href;
            expireDate.setTime(expireDate.getTime() + 10 * 60 * 1000);
            cookieUtil.createCookie(cookieName, value, expireDate);
        }
    };

    const hasCartItems = cartItemCount && cartItemCount > 0;

    const showAppLogoOnTinyScreen = hasCartItems;

    const showSaveSearch =
        (pageName?.includes('SearchResults') ||
            pageName?.includes('Category')) &&
        !isDesktop &&
        !isTablet;

    const profileLink = (
        <li className="slim-header__menu-option z-header">
            <ProfileLink
                handleProfileClick={handleProfileClick}
                menuExpanded={profileMenuOpen === MODAL_STATE.OPEN}
                hasLoggedIn={hasLoggedIn}
            />
        </li>
    );

    return (
        <>
            <Sticky
                top={0}
                enableTransforms={isSticky}
                activeClass="slim-header--sticky">
                <header className="slim-header d-hybrid-none" id="site-header">
                    <div
                        className="slim-header__content d-flex flex-wrap gap-1 gap-md-2 px-2 px-xl-5"
                        role="banner">
                        <DropdownMenu
                            className="navbar z-modal"
                            inSPA={inSPA}
                            isLoggedIn={isLoggedIn}
                            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                            tabIndex={1}
                        />
                        <div
                            className={`slim-header__logo d-flex ${
                                showAppLogoOnTinyScreen ? 'show-app-logo' : ''
                            } ${hasCartItems ? 'has-cart-items' : ''}`}
                            id="slim-header__logo">
                            <ALink
                                className="d-flex align-items-center focus-visible"
                                useLink={inSPA}
                                aria-label="Home"
                                href={toLocalizedUrl('/')}>
                                <TraderaAppLogo className="z-modal slim-header__logo--app" />
                                <TraderaLogo className="z-modal slim-header__logo--normal" />
                            </ALink>
                        </div>
                        <div
                            className={classNames(
                                ` slim-header__search d-none d-md-flex mt-1 mt-md-0 justify-content-center order-4 order-md-0 w-100`,
                                {
                                    'd-flex':
                                        showSearchForm && showHeaderSearch,
                                    'hide-search': !showHeaderSearch
                                }
                            )}
                            aria-hidden={!showHeaderSearch}
                            id="slim-header__search">
                            <Search inSPA={inSPA} />
                            {showSaveSearch && (
                                <SaveSearchButton
                                    type="icon"
                                    containerClassName="mr-n2"
                                    id={undefined}
                                />
                            )}
                        </div>
                        <nav role="navigation" className="ml-auto">
                            <ul className="plain-list d-flex gap-1 gap-lg-2">
                                {!isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-lg-flex">
                                        <LocalizationLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.LOCALIZATION
                                            }
                                        />
                                    </li>
                                )}
                                {!isLoggedIn && showCurrencySelection && (
                                    <li className="slim-header__menu-option z-modal d-none d-lg-flex">
                                        <CurrencyLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            onClickMenuOption={closeHoverMenu}
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.CURRENCY
                                            }
                                        />
                                    </li>
                                )}
                                {showHeaderSearch && isLoggedIn && (
                                    <li className="slim-header__menu-option slim-header__menu-option-search d-flex d-md-none align-items-center">
                                        <SearchButton
                                            showSearch={showSearchForm}
                                            onClick={handleClickSearch}
                                        />
                                    </li>
                                )}
                                {!isUnderage && (
                                    <li
                                        className={`slim-header__menu-option z-modal ${
                                            isLoggedIn
                                                ? `order-lg-1 ${
                                                      showHeaderSearch
                                                          ? 'd-none d-md-flex'
                                                          : 'd-flex'
                                                  }`
                                                : 'd-none d-sm-flex'
                                        }`}>
                                        <NewListingLink
                                            isLoggedIn={isLoggedIn}
                                            isDesktop={isDesktop}
                                        />
                                    </li>
                                )}
                                {hasCartItems ? (
                                    <li className="slim-header__menu-option z-modal">
                                        <CartLink
                                            cartItemCount={cartItemCount}
                                        />
                                    </li>
                                ) : null}
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-header">
                                        <NotificationsLink />
                                    </li>
                                )}
                                <li className="slim-header__menu-option z-modal">
                                    {isLoggedIn ? (
                                        <WishlistLink />
                                    ) : (
                                        <HowTraderaWorks
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.HOW_TRADERA_WORKS
                                            }
                                        />
                                    )}
                                </li>
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-md-flex align-items-end">
                                        <BuyingLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.BUY
                                            }
                                        />
                                    </li>
                                )}
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-lg-flex align-items-end">
                                        <SellingLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.SELL
                                            }
                                        />
                                    </li>
                                )}
                                {!hasLoggedIn && profileLink}
                                {!isLoggedIn && (
                                    <li className="slim-header__menu-option z-header d-flex align-items-end">
                                        <SignUpLink
                                            clickAction={handleRegisterClick}
                                            hasLoggedIn={hasLoggedIn}
                                        />
                                    </li>
                                )}
                                {hasLoggedIn && profileLink}
                            </ul>
                        </nav>
                    </div>
                </header>
                <LoaderLine isActive={isPageLoading} />
            </Sticky>
            {
                // NewMessageModal can summon a BankIdModal. BankId is a bottom sheet on mobile
                // so we need to render it outside the sticky header to avoid issues with it not
                // being rendered at the bottom of the viewport
                newMessageModal.open === MODAL_STATE.OPEN && (
                    <NewMessageModal
                        onModalClosed={() => dispatch(closeNewMessageModal())}
                        {...newMessageModal}
                    />
                )
            }
            <ProfileMenu show={profileMenuOpen === MODAL_STATE.OPEN} />
            <LocalizationRegionPicker />
            {isLoggedIn && <NotificationsSideSheet />}
            {isLoggedIn && <WishListSideSheet />}
            {isLoggedIn && <AppDownloadModal />}
            <PushNotificationModal />
            <ReferralModal />
            <TraderaToastContainer />
            <HeaderInternationalIdentificationModal />
            <div
                id="measure-100vh"
                style={{ height: '100vh', position: 'absolute' }}
            />
        </>
    );
};
